/**
 * Human-readable error banners for React Query failures.
 *
 * Every page that shows a "couldn't load" state should call this instead of
 * `err.message`. A 403 needs different copy than a 404 needs different copy
 * than a 500 needs different copy than "network down". Rendering the raw
 * message (which is often "Insufficient role for this action.") in every
 * error state confuses the user.
 */

import { ApiError, AbortedError } from "@/lib/errors"

/**
 * What the failed request was trying to do.
 *
 * The copy differs: a failed read leaves the screen empty and the reader
 * retries, while a failed write leaves them wondering whether their edit
 * survived — which is the first thing that sentence has to answer.
 */
export type ErrorAction = "load" | "save"

export interface ErrorBanner {
  /** Short one-line title suitable for a heading. */
  title: string
  /** Prose sentence — safe to render as body text. */
  body: string
  /** Whether the failure is expected to recover on a manual retry. */
  transient: boolean
}

const AUTH_TITLE = "You don't have access"
const NOT_FOUND_TITLE = "Not found"
const SERVER_TITLE = "Backend error"
const NETWORK_TITLE = "Can't reach the server"
const UNKNOWN_TITLE = "Something went wrong"

/**
 * Map an unknown error into a banner. Best-effort — an error we can't classify
 * still gets sensible copy rather than the raw string.
 */
export function bannerFor(
  err: unknown,
  resource?: string,
  action: ErrorAction = "load",
): ErrorBanner {
  const label = resource ? ` ${resource}` : ""
  const saving = action === "save"
  if (err instanceof AbortedError) {
    // Abort is expected on unmount / navigation; callers should not surface it.
    return { title: "", body: "", transient: false }
  }
  if (err instanceof ApiError) {
    if (err.status === 401) {
      return {
        title: "Your session ended",
        body: "You've been signed out. Sign in again to continue.",
        transient: false,
      }
    }
    if (err.status === 403) {
      return {
        title: AUTH_TITLE,
        body: saving
          ? `Changing${label} needs a different role. Nothing was saved.`
          : `Loading${label} needs a different role. Sign in with an account that has permission and try again.`,
        transient: false,
      }
    }
    if (err.status === 404) {
      return {
        title: NOT_FOUND_TITLE,
        body: `We couldn't find${label} — it may have been archived or the link is stale.`,
        transient: false,
      }
    }
    if (err.status === 429) {
      const wait = err.retryAfterSeconds
      return {
        title: "Too many requests",
        body: wait ? `Try again in ${String(wait)}s.` : "Slow down and try again in a moment.",
        transient: true,
      }
    }
    if (err.status >= 500) {
      return {
        title: SERVER_TITLE,
        // Never the backend's own `detail` here: a 500 carries "Internal Server
        // Error", or a stack-shaped sentence, neither of which tells a reviewer
        // what happened to their work.
        body: saving
          ? `The server could not save your change to${label}. Nothing was recorded, so it is safe to try again.`
          : `The server hit an error while loading${label}. Try again in a moment.`,
        transient: true,
      }
    }
    if (err.status === 0) {
      return {
        title: NETWORK_TITLE,
        body: "Check your connection and try again.",
        transient: true,
      }
    }
    return { title: UNKNOWN_TITLE, body: err.message, transient: true }
  }
  const message = err instanceof Error ? err.message : String(err)
  return { title: UNKNOWN_TITLE, body: message, transient: true }
}
